import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	PageHeader,
	useConfirmModal,
} from '@practium/ui';
import { TestimonialForm } from '../components/Forms/TestimonialForm/TestimonialForm';
import type { TestimonialFormRef } from '../components/Forms/TestimonialForm/TestimonialForm';
import { AvatarCard } from '@practium/features/users';
import { usePermissions } from '@practium/features/permissions';
import type { TestimonialFormData } from '../types/landing.types';
import { LANDING_ROUTES } from '../constants/landing.constants';
import styles from './AddTestimonialPage.module.css';

export const AddTestimonialPage = () => {
	const navigate = useNavigate();
	const { abilities, isLoading: permissionsLoading } = usePermissions();
	const canCreate = abilities.can('create', 'testimonials');

	useEffect(() => {
		if (!permissionsLoading && !canCreate) navigate(LANDING_ROUTES.CONTROL, { replace: true });
	}, [permissionsLoading, canCreate, navigate]);

	const [isLoading, setIsLoading] = useState(false);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [liveForm, setLiveForm] = useState<TestimonialFormData>({
		name: '',
		occupation: '',
		testimonial: '',
		status: '',
	});

	const formRef = useRef<TestimonialFormRef>(null);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const handleSubmit = useCallback(() => {
		navigate(LANDING_ROUTES.CONTROL);
	}, [navigate]);

	const handleCancel = useCallback(async () => {
		if (!avatarFile && !formRef.current?.isDirty?.()) {
			navigate(LANDING_ROUTES.CONTROL);
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (confirmed) navigate(LANDING_ROUTES.CONTROL);
	}, [avatarFile, confirm, navigate]);

	const handleSave = useCallback(() => formRef.current?.submit(), []);

	if (permissionsLoading || !canCreate) return null;

	return (
		<div className={styles.page}>
			<PageHeader
				title="Novo Depoimento"
				subtitle="Cadastre os dados do novo depoimento"
				onBack={() => navigate(LANDING_ROUTES.CONTROL)}
			/>
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Gerais"
						className={styles.mainCard}
					>
						<TestimonialForm
							ref={formRef}
							mode="add"
							avatarFile={avatarFile}
							onSubmit={handleSubmit}
							onLoadingChange={setIsLoading}
							onFormChange={setLiveForm}
						/>
					</FormCard>
					<div className={styles.pageActions}>
						<FormActions
							mode="add"
							onCancel={handleCancel}
							onSave={handleSave}
							isLoading={isLoading}
						/>
					</div>
				</div>
				<div className={styles.editSide}>
					<AvatarCard
						mode="add"
						file={avatarFile}
						onFileChange={setAvatarFile}
						personName={liveForm.name || undefined}
					/>
				</div>
			</div>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
